import { useEffect, useRef, useState } from "react"
import { createFileRoute } from "@tanstack/react-router"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"
import { Loader2, RotateCcw, Upload } from "lucide-react"
import { useActiveEvent } from "@/core/active-event"
import { MAX_ATTACHMENT_BYTES, formatBytes, uploadPublicImage } from "@/core/attachments"
import {
  eventSettingsQueryOptions,
  parseThemeOverride,
  patchEvent,
  serializeThemeOverride,
  type EventSettingsRow,
  type EventThemeTokens,
  type ThemeModeTokens,
} from "@/core/settings"
import { ApiError } from "@/core/api"
import { SquareCropDialog } from "@/components/settings/SquareCropDialog"
import {
  Card,
  Field,
  GhostButton,
  PrimaryButton,
  Select,
  SettingsPage,
  SettingsPageFooter,
  TextInput,
  Toggle,
} from "@/components/settings/primitives"
import { cn } from "@/core/utils"

export const Route = createFileRoute("/$orgSlug/$eventSlug/settings/branding")({
  component: BrandingView,
})

const HEX = /^#[0-9a-fA-F]{6}$/

function isHttpsUrl(value: string): boolean {
  if (!value.trim()) return true
  try {
    return new URL(value).protocol === "https:"
  } catch {
    return false
  }
}

type FormState = {
  hostName: string
  primaryColor: string
  appHeaderDesign: "default" | "compact" | "basic-whitelabel"
  appHeaderImageUrl: string
  appHeaderLogoUrl: string
  appHeaderShowLogo: boolean
  appHeaderShowEventName: boolean
  appHeaderShowDates: boolean
  appHeaderShowLocation: boolean
  appHeaderShowPoweredBy: boolean
  theme: EventThemeTokens | null
}

function BrandingView() {
  const { eventId } = useActiveEvent()
  const qc = useQueryClient()
  const q = useQuery({ ...eventSettingsQueryOptions(eventId ?? ""), enabled: !!eventId })

  const [form, setForm] = useState<FormState | null>(null)
  const [dirty, setDirty] = useState(false)
  const [uploading, setUploading] = useState(false)
  // The picked file awaiting a square crop before it uploads.
  const [pendingFile, setPendingFile] = useState<File | null>(null)
  const fileRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (!q.data || dirty) return
    setForm({
      hostName: q.data.hostName ?? "",
      primaryColor: q.data.primaryColor ?? "",
      appHeaderDesign:
        q.data.appHeaderDesign === "compact" || q.data.appHeaderDesign === "basic-whitelabel"
          ? q.data.appHeaderDesign
          : "default",
      appHeaderImageUrl: q.data.appHeaderImageUrl ?? "",
      appHeaderLogoUrl: q.data.appHeaderLogoUrl ?? "",
      appHeaderShowLogo: q.data.appHeaderShowLogo !== 0,
      appHeaderShowEventName: q.data.appHeaderShowEventName !== 0,
      appHeaderShowDates: q.data.appHeaderShowDates !== 0,
      appHeaderShowLocation: q.data.appHeaderShowLocation !== 0,
      appHeaderShowPoweredBy: q.data.appHeaderShowPoweredBy !== 0,
      theme: parseThemeOverride(q.data.themeOverride),
    })
  }, [q.data, dirty])

  const save = useMutation({
    mutationFn: () => {
      if (!eventId || !form) throw new Error("Not ready")
      if (form.primaryColor && !HEX.test(form.primaryColor))
        throw new ApiError("Primary color must be a #RRGGBB hex value", 400)
      if (!isHttpsUrl(form.appHeaderImageUrl))
        throw new ApiError("App header image must use a valid HTTPS URL", 400)
      if (!isHttpsUrl(form.appHeaderLogoUrl))
        throw new ApiError("Custom logo must use a valid HTTPS URL", 400)
      const patch: Partial<EventSettingsRow> = {
        hostName: form.hostName.trim() || null,
        primaryColor: form.primaryColor.trim() || null,
        appHeaderDesign: form.appHeaderDesign,
        appHeaderImageUrl: form.appHeaderImageUrl.trim() || null,
        appHeaderLogoUrl: form.appHeaderLogoUrl.trim() || null,
        appHeaderShowLogo: form.appHeaderShowLogo ? 1 : 0,
        appHeaderShowEventName: form.appHeaderShowEventName ? 1 : 0,
        appHeaderShowDates: form.appHeaderShowDates ? 1 : 0,
        appHeaderShowLocation: form.appHeaderShowLocation ? 1 : 0,
        appHeaderShowPoweredBy: form.appHeaderShowPoweredBy ? 1 : 0,
        // Accent is a single control ("Primary color") — fold it into the
        // per-mode theme tokens on the way out so the guest apps (which read the
        // theme's accent) stay in sync, without a duplicate swatch in the editor.
        themeOverride: serializeThemeOverride(seedAccent(form.theme, form.primaryColor)),
      }
      return patchEvent(eventId, patch)
    },
    onSuccess: () => {
      setDirty(false)
      toast.success("Branding saved")
      void qc.invalidateQueries({ queryKey: ["resource", "events", eventId] })
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : "Failed to save branding"),
  })

  const update = (patch: Partial<FormState>) => {
    setDirty(true)
    setForm((f) => (f ? { ...f, ...patch } : f))
  }

  // Pick → square-crop → upload. Validation runs on the source file; the crop
  // dialog opens, and only its cropped output hits the network.
  const onPickFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = "" // allow re-picking the same file
    if (!file || !eventId) return
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file")
      return
    }
    if (file.size > MAX_ATTACHMENT_BYTES) {
      toast.error(`Image too large — max ${formatBytes(MAX_ATTACHMENT_BYTES)}`)
      return
    }
    setPendingFile(file)
  }

  // Direct-to-R2 upload of the cropped square → sets the URL field to the public
  // files.attend.vip URL (reuses the deployed presigned-PUT media pipeline). The
  // user still has to "Save changes" to persist it — consistent with every other
  // field here.
  const onCropped = async (cropped: File) => {
    if (!eventId) return
    setPendingFile(null)
    setUploading(true)
    try {
      const url = await uploadPublicImage(eventId, cropped)
      update({ appHeaderImageUrl: url })
      toast.success("App header image uploaded — Save changes to apply")
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Upload failed")
    } finally {
      setUploading(false)
    }
  }

  return (
    <SettingsPage
      title="Branding & theme"
      description="The accent, attendee-app hero and optional per-mode theme override for this event."
    >
      {q.isError && (
        <Card>
          <p className="text-[13px] text-rose-600 dark:text-rose-400">
            {(q.error as Error)?.message ?? "Failed to load branding"}
          </p>
        </Card>
      )}

      <Card title="Branding">
        <Field
          label="Organization eyebrow"
          hint="Shown above the event name when no event brand kit or assigned client name overrides it."
        >
          <TextInput
            value={form?.hostName ?? ""}
            disabled={!form}
            placeholder="Organization name"
            onChange={(e) => update({ hostName: e.target.value })}
          />
        </Field>
        <Field label="Primary color" hint="The one accent — used across chrome, buttons, and the guest apps.">
          <div className="flex items-center gap-2.5">
            <input
              type="color"
              value={HEX.test(form?.primaryColor ?? "") ? form!.primaryColor : "#015cfe"}
              disabled={!form}
              onChange={(e) => update({ primaryColor: e.target.value })}
              className="h-9 w-12 shrink-0 cursor-pointer rounded-lg border border-plum-200/70 bg-white p-1 dark:border-white/10 dark:bg-white/5"
            />
            <TextInput
              value={form?.primaryColor ?? ""}
              disabled={!form}
              placeholder="#015cfe"
              onChange={(e) => update({ primaryColor: e.target.value })}
              className={cn(
                "font-mono",
                form?.primaryColor && !HEX.test(form.primaryColor)
                  ? "border-rose-400 focus:border-rose-400 focus:ring-rose-400/20"
                  : "",
              )}
            />
          </div>
        </Field>
        <Field label="App header image" hint="Used by the attendee app. Upload a square image — it's cropped to square on upload.">
          <div className="flex items-center gap-2.5">
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              hidden
              onChange={onPickFile}
            />
            <GhostButton
              type="button"
              disabled={!form || uploading}
              onClick={() => fileRef.current?.click()}
              className="shrink-0"
            >
              {uploading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Upload className="h-4 w-4" />
              )}
              {uploading ? "Uploading…" : form?.appHeaderImageUrl ? "Replace" : "Upload"}
            </GhostButton>
            {form?.appHeaderImageUrl && !uploading && (
              <GhostButton
                type="button"
                disabled={!form}
                onClick={() => update({ appHeaderImageUrl: "" })}
                className="shrink-0"
              >
                Remove
              </GhostButton>
            )}
          </div>
        </Field>
        {form?.appHeaderImageUrl && (
          <img
            src={form.appHeaderImageUrl}
            alt="App header preview"
            className="h-auto w-full rounded-lg object-contain ring-1 ring-plum-200/50 dark:ring-white/10"
            onError={(e) => (e.currentTarget.style.display = "none")}
          />
        )}

        {pendingFile && (
          <SquareCropDialog
            file={pendingFile}
            onCancel={() => setPendingFile(null)}
            onCropped={onCropped}
          />
        )}

        <ThemeOverrideEditor
          value={form?.theme ?? null}
          disabled={!form}
          onChange={(theme) => update({ theme })}
        />
      </Card>

      <Card
        title="Attendee app hero"
        description="Choose the Home header design, identity and event details."
      >
        <div className="space-y-2.5">
          <Field
            label="Header design"
            hint="Default is the full photo hero. Compact is shorter. Basic-Whitelabel puts the event name beside Back and omits the logo."
          >
            <Select
              value={form?.appHeaderDesign ?? "default"}
              disabled={!form}
              onChange={(e) =>
                update({
                  appHeaderDesign:
                    e.target.value === "compact" || e.target.value === "basic-whitelabel"
                      ? e.target.value
                      : "default",
                })
              }
            >
              <option value="default">Default</option>
              <option value="compact">Compact</option>
              <option value="basic-whitelabel">Basic-Whitelabel</option>
            </Select>
          </Field>
          <Toggle
            label="Show logo"
            description="Uses the URL below, then the assigned brand-kit logo, then Attend. Turn off for no logo."
            checked={form?.appHeaderShowLogo ?? true}
            disabled={!form}
            onChange={(value) => update({ appHeaderShowLogo: value })}
          />
          {form?.appHeaderShowLogo && (
            <Field
              label="Custom logo URL"
              hint="Optional HTTPS image URL. Leave blank to use the event brand or Attend wordmark."
            >
              <TextInput
                type="url"
                value={form.appHeaderLogoUrl}
                disabled={!form}
                placeholder="https://files.example.com/wordmark.png"
                onChange={(e) => update({ appHeaderLogoUrl: e.target.value })}
              />
            </Field>
          )}
          <Toggle
            label="Show event name"
            checked={form?.appHeaderShowEventName ?? true}
            disabled={!form}
            onChange={(value) => update({ appHeaderShowEventName: value })}
          />
          <Toggle
            label="Show dates"
            checked={form?.appHeaderShowDates ?? true}
            disabled={!form}
            onChange={(value) => update({ appHeaderShowDates: value })}
          />
          <Toggle
            label="Show location"
            checked={form?.appHeaderShowLocation ?? true}
            disabled={!form}
            onChange={(value) => update({ appHeaderShowLocation: value })}
          />
          <Toggle
            label="Show Powered by Attend"
            description="Displays a quiet Attend credit at the bottom of Event Home."
            checked={form?.appHeaderShowPoweredBy ?? true}
            disabled={!form}
            onChange={(value) => update({ appHeaderShowPoweredBy: value })}
          />
        </div>
      </Card>

      <SettingsPageFooter>
        <PrimaryButton onClick={() => save.mutate()} disabled={!dirty || save.isPending}>
          {save.isPending ? "Saving…" : "Save changes"}
        </PrimaryButton>
      </SettingsPageFooter>
    </SettingsPage>
  )
}

// The per-mode theme editor no longer exposes "accent" — it's set once via
// "Primary color" and seeded into both modes at save (seedAccent below).
const TOKENS: { key: keyof ThemeModeTokens; label: string }[] = [
  { key: "backgroundTop", label: "Background top" },
  { key: "backgroundBottom", label: "Background bottom" },
  { key: "surface", label: "Surface" },
  { key: "textPrimary", label: "Text primary" },
  { key: "textSecondary", label: "Text secondary" },
]

// Stamp the single chosen accent (Primary color) onto whichever per-mode
// palettes are customized, so the guest apps' theme accent tracks Primary color.
// Untouched when there's no override (null) or no valid hex.
function seedAccent(theme: EventThemeTokens | null, primaryColor: string): EventThemeTokens | null {
  if (!theme) return theme
  const accent = HEX.test(primaryColor.trim()) ? primaryColor.trim() : undefined
  if (!accent) return theme
  const stamp = (m?: ThemeModeTokens) =>
    m && Object.keys(m).length > 0 ? { ...m, accent } : m
  return { light: stamp(theme.light), dark: stamp(theme.dark) }
}

// Default "Attend" palette = the VIP attendee look (the blues), mirroring
// apps/guest-ios ThemePalette.vipLight / vipDark. These tokens drive the VIP
// guest apps (iOS + Android), so the seed must be VIP blue — NOT the AAA staff
// eggplant (#492478). Per-mode: light is a soft blue wash on white; dark is the
// signature deep-blue atmosphere on white text. Same #015cfe accent in both.
const DEFAULT_MODES: Record<"light" | "dark", ThemeModeTokens> = {
  light: {
    backgroundTop: "#e9eeff",
    backgroundBottom: "#f7f7fa",
    surface: "#ffffff",
    accent: "#015cfe",
    textPrimary: "#0b0a24",
    textSecondary: "#5a5a6e",
  },
  dark: {
    backgroundTop: "#04006c",
    backgroundBottom: "#02011b",
    surface: "#0a0848",
    accent: "#015cfe",
    textPrimary: "#ffffff",
    textSecondary: "#b8b9cc",
  },
}

function ThemeOverrideEditor({
  value,
  disabled,
  onChange,
}: {
  value: EventThemeTokens | null
  disabled?: boolean
  onChange: (next: EventThemeTokens | null) => void
}) {
  const [mode, setMode] = useState<"light" | "dark">("light")
  const modeTokens = value?.[mode] ?? null
  const customized = !!modeTokens && Object.keys(modeTokens).length > 0

  const setModeTokens = (next: ThemeModeTokens | null) => {
    const updated: EventThemeTokens = { ...(value ?? {}) }
    if (next) updated[mode] = next
    else delete updated[mode]
    const hasAny = (updated.light && Object.keys(updated.light).length) || (updated.dark && Object.keys(updated.dark).length)
    onChange(hasAny ? updated : null)
  }

  return (
    <div className="space-y-3 border-t border-plum-200/50 pt-4 dark:border-white/10">
      <div>
        <h3 className="text-[13px] font-semibold text-plum-900 dark:text-plum-50">Theme override</h3>
        <p className="mt-0.5 text-[12px] text-plum-700/55 dark:text-plum-200/40">
          Override the per-mode palette — background, surface and text. The accent
          comes from Primary color above. Off uses Attend's defaults.
        </p>
      </div>

      <div className="inline-flex rounded-lg border border-plum-200/60 p-0.5 dark:border-white/10">
        {(["light", "dark"] as const).map((m) => (
          <button
            key={m}
            type="button"
            onClick={() => setMode(m)}
            className={cn(
              "rounded-md px-3 py-1 text-[12.5px] font-semibold capitalize transition",
              mode === m
                ? "bg-[var(--accent)] text-white"
                : "text-plum-700/60 hover:text-plum-900 dark:text-plum-200/50 dark:hover:text-plum-100",
            )}
          >
            {m}
          </button>
        ))}
      </div>

      <Toggle
        label={`Custom ${mode} palette`}
        description={customized ? "Tokens below override Attend defaults." : "Using Attend defaults."}
        checked={customized}
        disabled={disabled}
        onChange={(on) => setModeTokens(on ? { ...DEFAULT_MODES[mode] } : null)}
      />

      {customized && modeTokens && (
        <div className="grid gap-3 @[560px]:grid-cols-2">
          {TOKENS.map((t) => {
            const hex = modeTokens[t.key] ?? DEFAULT_MODES[mode][t.key] ?? "#000000"
            return (
              <div key={t.key} className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <label className="text-[12px] font-semibold text-plum-800 dark:text-plum-100">
                    {t.label}
                  </label>
                  <button
                    type="button"
                    title="Reset to default"
                    disabled={disabled}
                    onClick={() => setModeTokens({ ...modeTokens, [t.key]: DEFAULT_MODES[mode][t.key] })}
                    className="text-plum-700/40 transition hover:text-plum-700 dark:text-plum-200/30 dark:hover:text-plum-200"
                  >
                    <RotateCcw className="h-3.5 w-3.5" />
                  </button>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={HEX.test(hex) ? hex : "#000000"}
                    disabled={disabled}
                    onChange={(e) => setModeTokens({ ...modeTokens, [t.key]: e.target.value })}
                    className="h-8 w-10 shrink-0 cursor-pointer rounded-md border border-plum-200/70 bg-white p-0.5 dark:border-white/10 dark:bg-white/5"
                  />
                  <TextInput
                    className="h-8 font-mono"
                    value={hex}
                    disabled={disabled}
                    onChange={(e) => setModeTokens({ ...modeTokens, [t.key]: e.target.value })}
                  />
                </div>
              </div>
            )
          })}
          <div className="@[560px]:col-span-2">
            <GhostButton className="h-8" disabled={disabled} onClick={() => setModeTokens(null)}>
              Use Attend defaults
            </GhostButton>
          </div>
        </div>
      )}
    </div>
  )
}
